
import { useState } from "react";
import { Share2, Users, UserPlus, CheckCircle2, Loader2, Send, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/lib/supabase';

interface ShareAfterEntryPopupProps {
  isOpen: boolean;
  onClose: () => void;
  competitionId: number;
  competitionTitle: string;
  referralUrl?: string;
}

// Type for the friend object in the invite form
interface Friend {
  id: number;
  name: string;
  email: string;
  phone: string;
  sending: boolean;
  sent: boolean;
  phoneError: string;
}

export default function ShareAfterEntryPopup({
  isOpen,
  onClose,
  competitionId,
  competitionTitle,
  referralUrl
}: ShareAfterEntryPopupProps) {
  const { toast } = useToast();
  const [friendInvites, setFriendInvites] = useState<Friend[]>([
    { id: 1, name: '', email: '', phone: '', sending: false, sent: false, phoneError: '' },
  ]);
  const [successfulInvitesCount, setSuccessfulInvitesCount] = useState(0);

  // Reset form when modal is closed
  const handleClose = () => {
    setFriendInvites([{ id: 1, name: '', email: '', phone: '', sending: false, sent: false, phoneError: '' }]);
    setSuccessfulInvitesCount(0);
    onClose();
  };

  // Function to add more friend invite rows
  const addMoreFriendRows = () => {
    const newId = Math.max(...friendInvites.map(f => f.id)) + 1;
    setFriendInvites([...friendInvites, { id: newId, name: '', email: '', phone: '', sending: false, sent: false, phoneError: '' }]);
  };

  // Function to update a friend invite
  const updateFriendInvite = (id: number, field: string, value: string) => {
    setFriendInvites(prev => 
      prev.map(invite => 
        invite.id === id 
          ? { ...invite, [field]: value, ...(field === 'phone' ? { phoneError: '' } : {}) }
          : invite
      )
    );
  };

  // Function to remove a friend invite row
  const removeFriendInvite = (id: number) => {
    if (friendInvites.length > 1) {
      setFriendInvites(prev => prev.filter(invite => invite.id !== id));
    }
  };

  // South African phone number validation
  const validateSouthAfricanPhone = (phone: string) => {
    if (!phone || phone.trim() === '') return { isValid: false, message: 'Phone number is required' };

    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    const phoneRegex = /^(\+27|0)[6-8][0-9]{8}$/;

    if (!phoneRegex.test(cleanPhone)) {
      return { 
        isValid: false, 
        message: 'Please enter a valid SA number (e.g., 0821234567 or +27821234567)' 
      };
    }

    return { isValid: true, message: '' };
  };

  // Function to send individual invite
  const sendIndividualInvite = async (inviteId: number) => {
    const invite = friendInvites.find(inv => inv.id === inviteId);
    if (!invite) return;

    // Validate required fields
    if (!invite.name.trim()) {
      toast({
        title: "Missing Name",
        description: "Please enter your friend's name.",
        variant: "destructive",
      });
      return;
    }

    // Validate phone number
    const phoneValidation = validateSouthAfricanPhone(invite.phone);
    if (!phoneValidation.isValid) {
      setFriendInvites(prev => 
        prev.map(inv => 
          inv.id === inviteId 
            ? { ...inv, phoneError: phoneValidation.message }
            : inv
        )
      );
      toast({
        title: "Invalid Phone Number",
        description: phoneValidation.message,
        variant: "destructive",
      });
      return;
    }

    // Set sending state
    setFriendInvites(prev => 
      prev.map(inv => 
        inv.id === inviteId 
          ? { ...inv, sending: true, phoneError: '' }
          : inv
      )
    );

    try {
      // Get the auth token properly
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast({
          title: "Authentication Error",
          description: "Please log in to send invitations",
          variant: "destructive",
        });
        setFriendInvites(prev => prev.map((f, i) => 
          f.id === inviteId ? { ...f, sending: false } : f
        ));
        return;
      }

      const response = await fetch('/api/friends/invite-prizedrop', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          friendName: invite.name.trim(),
          friendEmail: invite.email.trim() || undefined,
          friendPhone: invite.phone.trim(),
          competitionId: competitionId // Send competition-specific referral link
        })
      });

      let responseData;
      const responseText = await response.text();

      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('Invalid JSON response:', responseText);
        throw new Error('Invalid server response');
      }

      if (response.ok) {
        setSuccessfulInvitesCount(prev => prev + 1);

        // Remove the successfully sent invite row to show a clean form
        setFriendInvites(prev => {
          const updated = prev.filter(inv => inv.id !== inviteId);
          // If all invites were removed, add a fresh empty one
          if (updated.length === 0) {
            return [{ id: 1, name: '', email: '', phone: '', sending: false, sent: false, phoneError: '' }];
          }
          return updated;
        });

        toast({
          title: "Invitation Sent! 🎉",
          description: `${invite.name} will receive an SMS invitation to join this competition`,
        });
      } else {
        toast({
          title: "Error",
          description: responseData?.error || `Server error: ${response.status}`,
          variant: "destructive",
        });
        setFriendInvites(prev => 
          prev.map(inv => 
            inv.id === inviteId 
              ? { ...inv, sending: false }
              : inv
          )
        );
      }
    } catch (error: any) {
      console.error('Friend invitation error:', error);
      toast({
        title: "Network Error",
        description: error.message || "Failed to send invitation",
        variant: "destructive",
      });
      setFriendInvites(prev => 
        prev.map(inv => 
          inv.id === inviteId 
            ? { ...inv, sending: false }
            : inv
        )
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        handleClose();
      }
    }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto mx-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Users className="w-6 h-6 text-primary" />
            Invite a friend and both of you earn 35 bonus entries
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">

          {/* SMS Invite Form */}
          <div className="space-y-3">
            {/* Header row - Hidden on mobile */}
            <div className="hidden md:grid grid-cols-12 gap-2 px-2 text-sm font-medium text-gray-700">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Email (Optional)</div>
              <div className="col-span-3">Phone Number</div>
              <div className="col-span-3"></div>
            </div>

            {friendInvites.map((invite, index) => (
              <div key={invite.id} className="space-y-3 p-3 md:p-4 border border-gray-200 rounded-lg bg-gray-50">
                {/* Mobile Layout */}
                <div className="block md:hidden space-y-3">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-600 mb-1">Name *</label>
                    <input
                      type="text"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${invite.sent ? 'border-green-300' : 'border-gray-300 focus:ring-blue-500'}`}
                      placeholder="John Doe"
                      value={invite.name}
                      onChange={(e) => updateFriendInvite(invite.id, 'name', e.target.value)}
                      disabled={invite.sending || invite.sent}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-600 mb-1">Email (Optional)</label>
                    <input
                      type="email"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${invite.sent ? 'border-green-300' : 'border-gray-300 focus:ring-blue-500'}`}
                      placeholder="friend@example.com"
                      value={invite.email}
                      onChange={(e) => updateFriendInvite(invite.id, 'email', e.target.value)}
                      disabled={invite.sending || invite.sent}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-600 mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${
                        invite.phoneError 
                          ? 'border-red-300 focus:ring-red-500' 
                          : invite.sent 
                            ? 'border-green-300' 
                            : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="27821234567"
                      value={invite.phone}
                      onChange={(e) => updateFriendInvite(invite.id, 'phone', e.target.value)}
                      disabled={invite.sending || invite.sent}
                    />
                    {invite.phoneError && (
                      <p className="text-red-500 text-xs mt-1">{invite.phoneError}</p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => sendIndividualInvite(invite.id)}
                      disabled={invite.sending || invite.sent}
                      className={`w-full h-12 transition-colors ${
                        invite.sent 
                          ? 'bg-green-600 hover:bg-green-700' 
                          : 'bg-blue-600 hover:bg-blue-700'
                      } text-white`}
                    >
                      {invite.sending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : invite.sent ? (
                        <>
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Sent!
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send Invite
                        </>
                      )}
                    </Button>
                    {friendInvites.length > 1 && (
                      <Button
                        variant="outline"
                        onClick={() => removeFriendInvite(invite.id)}
                        className="w-full h-10 text-red-600 hover:text-red-700 hover:bg-red-50"
                        disabled={invite.sending}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Remove
                      </Button>
                    )}
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:grid grid-cols-12 gap-2 items-start">
                  <input
                    type="text"
                    className={`col-span-3 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${invite.sent ? 'border-green-300' : 'border-gray-300 focus:ring-blue-500'}`}
                    placeholder="John Doe"
                    value={invite.name}
                    onChange={(e) => updateFriendInvite(invite.id, 'name', e.target.value)}
                    disabled={invite.sending || invite.sent}
                  />

                  <input
                    type="email"
                    className={`col-span-3 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${invite.sent ? 'border-green-300' : 'border-gray-300 focus:ring-blue-500'}`}
                    placeholder="friend@example.com"
                    value={invite.email}
                    onChange={(e) => updateFriendInvite(invite.id, 'email', e.target.value)}
                    disabled={invite.sending || invite.sent}
                  />

                  <div className="col-span-3 space-y-1">
                    <input
                      type="tel"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${
                        invite.phoneError 
                          ? 'border-red-300 focus:ring-red-500' 
                          : invite.sent 
                            ? 'border-green-300' 
                            : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="0821234567"
                      value={invite.phone}
                      onChange={(e) => updateFriendInvite(invite.id, 'phone', e.target.value)}
                      disabled={invite.sending || invite.sent}
                    />
                    {invite.phoneError && (
                      <p className="text-red-500 text-xs">{invite.phoneError}</p>
                    )}
                  </div>

                  <div className="col-span-3 flex gap-1">
                    <Button
                      onClick={() => sendIndividualInvite(invite.id)}
                      disabled={invite.sending || invite.sent}
                      className={`flex-1 transition-colors ${
                        invite.sent 
                          ? 'bg-green-600 hover:bg-green-700' 
                          : 'bg-blue-600 hover:bg-blue-700'
                      } text-white`}
                      size="sm"
                    >
                      {invite.sending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          Sending
                        </>
                      ) : invite.sent ? (
                        <>
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Sent
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-1" />
                          Send
                        </>
                      )}
                    </Button>
                    {friendInvites.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeFriendInvite(invite.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        disabled={invite.sending}
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Add More button */}
            <Button
              variant="outline"
              onClick={addMoreFriendRows}
              className="w-full mt-4 h-12"
              disabled={friendInvites.some(invite => invite.sending)}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add More Friends
            </Button>
          </div>

          {/* Success Message */}
          {successfulInvitesCount > 0 && (
            <div className="text-center py-3">
              <div className="text-green-600 font-medium">
                <CheckCircle2 className="h-5 w-5 inline-block mr-1" />
                {successfulInvitesCount} invite(s) sent successfully!
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
